import { Badge, Group, Text, Tooltip } from '@mantine/core';

export interface SessionEngagementEntry {
  session_number: number;
  score: number | null;
  topic: string | null;
}

/**
 * Canonical engagement color scale — 5-tier, matches SessionsCellRenderers.tsx
 */
export const ENGAGEMENT_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  5: { bg: '#28463A', text: '#fff', label: '5 — Highly Engaged' },
  4: { bg: '#4A7C59', text: '#fff', label: '4 — Engaged' },
  3: { bg: '#E9A23B', text: '#fff', label: '3 — Moderate' },
  2: { bg: '#E07B4C', text: '#fff', label: '2 — Low' },
  1: { bg: '#D64545', text: '#fff', label: '1 — Disengaged' },
};

function buildTooltip(entry: SessionEngagementEntry): string {
  const base = `Session ${entry.session_number}`;
  if (entry.topic) return `${base}: ${entry.topic}`;
  return base;
}

/**
 * Single per-session engagement indicator pill.
 * Reuses the same visual treatment as the Sessions tab EngagementPill.
 */
export function SessionEngagementIndicator({ entry }: { entry: SessionEngagementEntry }) {
  const tooltip = buildTooltip(entry);

  if (entry.score === null) {
    return (
      <Tooltip label={tooltip} withArrow>
        <Badge
          size="sm"
          color="gray"
          variant="light"
          tabIndex={0}
          role="img"
          aria-label={`${tooltip} — No rating`}
          styles={{ label: { fontSize: 11, letterSpacing: 0.3, textTransform: 'none' } }}
        >
          —
        </Badge>
      </Tooltip>
    );
  }

  const cfg = ENGAGEMENT_COLORS[entry.score] || { bg: '#888', text: '#fff', label: String(entry.score) };

  return (
    <Tooltip label={tooltip} withArrow>
      <Badge
        size="sm"
        tabIndex={0}
        role="img"
        aria-label={`${tooltip} — ${cfg.label}`}
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
        }}
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
}

/**
 * Row of per-session engagement indicators for use in table cells.
 */
export function SessionEngagementIndicatorList({ entries }: { entries: SessionEngagementEntry[] }) {
  if (!entries || entries.length === 0) {
    return <Text size="xs" c="dimmed" fs="italic">No ratings</Text>;
  }

  return (
    <Group gap={4} wrap="wrap">
      {entries.map(entry => (
        <SessionEngagementIndicator key={entry.session_number} entry={entry} />
      ))}
    </Group>
  );
}
